import { useToast } from '@melio/penny';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useMelioIntl } from '@melio/platform-i18n';
import { useNavigate } from '@melio/platform-utils';
import { usePlans, useSubscriptionMe } from '@melio/subscriptions-api';
import { useSubscription } from '@melio/subscriptions-sdk';
import { useState } from 'react';

import { DEFAULT_DASHBOARD_ROUTE } from '../../constants';
import { useSubscriptionPlanTier } from '../../utils';
import { SubscriptionCancelOrDowngradeActivity } from '../SubscriptionCancelOrDowngrade';
import { SubscriptionUpgradeModalActivity } from '../SubscriptionUpgradeModal';
import { PlanUpgradeDetails } from '../SubscriptionUpgradeModal/types';
import { getSubscriptionCheckoutUrl } from '../SubscriptionWizard';
import { SwitchSubscriptionToYearlyModalScreen } from '../SwitchSubscriptionToYearly';
import { SubscriptionPlansScreen } from './screens/SubscriptionPlans.screen';
import { SubscribeToPlanType } from './types';

type SubscriptionPlansActivityProps = {
  returnUrl?: string;
  onFreePlanSubscribe?: () => void;
};

export const SubscriptionPlansActivity = ({ returnUrl, onFreePlanSubscribe }: SubscriptionPlansActivityProps) => {
  const [isPlatformSubscriptionsEnabled] = useFeature(FeatureFlags.PlatformSubscriptions, false);
  const { isFetching, data: plans = [] } = usePlans();
  const { create: createSubscription, isMutating: isSavingFreePlan } = useSubscriptionMe({
    enabled: isPlatformSubscriptionsEnabled,
  });
  const { subscription } = useSubscription();
  const { formatMessage } = useMelioIntl();
  const toast = useToast();
  const navigate = useNavigate();
  const [planUpgradeDetails, setPlanUpgradeDetails] = useState<PlanUpgradeDetails | null>(null);
  const [isCancelOrDowngradeModalOpen, setIsCancelOrDowngradeModalOpen] = useState<boolean>(false);
  const [isSwitchBillingCycleModalOpen, setIsSwitchBillingCycleModalOpen] = useState<boolean>(false);
  const [newPlanId, setNewPlanId] = useState<string>();
  const { freePlan, isDowngradedPlan, isUpgradedPlan } = useSubscriptionPlanTier();

  const onNewUserFreePlanSubscribe = (freePlanId: string) => {
    void createSubscription({ fundingSourceId: '-1', planId: freePlanId, numberOfSeats: -1 })
      .then(() => {
        if (onFreePlanSubscribe) {
          // leaves flow without the checkout page
          onFreePlanSubscribe();
        } else {
          navigate(returnUrl || DEFAULT_DASHBOARD_ROUTE);
        }
      })
      .catch(() => {
        toast({ type: 'error', title: formatMessage('activities.subscription.checkout.summary.toast.error') });
      });
  };

  const onSubscribe = ({ planId }: SubscribeToPlanType) => {
    if (planId === freePlan?.id) {
      onNewUserFreePlanSubscribe(planId);
    } else {
      navigate(
        {
          pathname: getSubscriptionCheckoutUrl(),
          search: `planId=${planId}`,
        },
        { state: { returnUrl } }
      );
    }
  };

  const onCancelOrDowngradePaidPlan = (planId: string) => {
    setNewPlanId(planId);
    setIsCancelOrDowngradeModalOpen(true);
  };

  const onUpgradeTier = ({ planId, billingCycle }: SubscribeToPlanType) => {
    setPlanUpgradeDetails({
      planId,
      billingCycle,
    });
  };

  const onSwitchBillingCycle = () => {
    setIsSwitchBillingCycleModalOpen(true);
  };

  const onSelectPlan = ({ planId, billingCycle }: SubscribeToPlanType) => {
    if (!subscription?.planId || subscription.planId === freePlan?.id) {
      return onSubscribe({ planId, billingCycle });
    }
    if (isDowngradedPlan(planId)) {
      return onCancelOrDowngradePaidPlan(planId);
    }
    if (isUpgradedPlan(planId)) {
      return onUpgradeTier({ planId, billingCycle });
    }
    onSwitchBillingCycle();
  };

  const onCloseCancelModal = () => setIsCancelOrDowngradeModalOpen(false);
  const onCloseSwitchModal = () => setIsSwitchBillingCycleModalOpen(false);

  return (
    <>
      <SubscriptionPlansScreen
        plans={plans}
        isLoading={isFetching}
        freePlanId={freePlan?.id}
        onSelectPlan={onSelectPlan}
        isSavingFreePlan={isSavingFreePlan}
      />
      <SubscriptionCancelOrDowngradeActivity
        isOpen={isCancelOrDowngradeModalOpen}
        newPlanId={newPlanId}
        onClose={onCloseCancelModal}
      />
      {planUpgradeDetails && (
        <SubscriptionUpgradeModalActivity
          isOpen={!!planUpgradeDetails}
          onClose={() => setPlanUpgradeDetails(null)}
          planUpgradeDetails={planUpgradeDetails}
        />
      )}
      <SwitchSubscriptionToYearlyModalScreen
        isOpen={isSwitchBillingCycleModalOpen}
        onClose={onCloseSwitchModal}
        onChange={() => {
          //TODO: implement switch to yearly flow
        }}
      />
    </>
  );
};
